import constate from 'constate';
import { createReducer } from 'helpers/reducers-helper';
import { useCallback, useEffect, useReducer } from 'react';
import merge from 'lodash/merge';
import { errorHandingWithCode, errorHandler } from 'helpers/error-handler';
import { repo } from 'index';
import { useHistory } from 'react-router-dom';
import {
  EMAIL,
  fieldCheck,
  REQUIRE,
  TWOFA,
  PASSWORD,
  CREDIT_CARD_NUMBER,
  CREDIT_CARD_EXPIRED_DATE,
  CREDIT_CARD_CVC,
} from 'helpers/rule-validation';
import { PATH } from 'config/path';
import analytics from 'service/analytics';

export const STEPS = {
  LOGIN: 'login',
  ONBOARDING: 'onboarding',
  ACTIVATION: 'activation',
  TWOFA_LOGIN: '2fa-login',
  RESET_PASSWORD: 'reset-password',
};

const SCOPES = {
  USER: 'user',
  USER_SUB_ACCOUNT: 'user_sub_account',
  UPDATE_USER_PASSWORD: 'update_user_password',
  USER_2FA_LOGIN: 'user_2fa_login',
};

// login
export const CHANGE_USERNAME = 'CHANGE_USERNAME';
export const CHANGE_USERNAME_ERROR_MESSAGE = 'CHANGE_USERNAME_ERROR_MESSAGE';
export const CHANGE_PASSWORD = 'CHANGE_PASSWORD';
export const CHANGE_PASSWORD_ERROR_MESSAGE = 'CHANGE_PASSWORD_ERROR_MESSAGE';
export const CHANGE_SUB_ACCOUNT = 'CHANGE_SUB_ACCOUNT';
// 2fa
export const CHANGE_TOTP = 'CHANGE_TOTP';
export const CHANGE_TOTP_ERROR_MESSAGE = 'CHANGE_TOTP_ERROR_MESSAGE';
// onboarding
export const CHANGE_NEW_PASSWORD = 'CHANGE_NEW_PASSWORD';
export const CHANGE_CONFIRMED_PASSWORD = 'CHANGE_CONFIRMED_PASSWORD';
export const CHANGE_NEW_PASSWORD_ERROR_MESSAGE = 'CHANGE_NEW_PASSWORD_ERROR_MESSAGE';
export const CHANGE_CONFIRMED_PASSWORD_ERROR_MESSAGE = 'CHANGE_CONFIRMED_PASSWORD_ERROR_MESSAGE';
export const CHANGE_EMAIL = 'CHANGE_EMAIL';
export const CHANGE_EMAIL_ERROR_MESSAGE = 'CHANGE_EMAIL_ERROR_MESSAGE';
export const CHANGE_COMPANY_NAME = 'CHANGE_COMPANY_NAME';
export const CHANGE_COMPANY_NAME_ERROR_MESSAGE = 'CHANGE_COMPANY_NAME_ERROR_MESSAGE';
export const SET_ONBOARDING_PASSWORD_STEP = 'SET_ONBOARDING_PASSWORD_STEP';
export const SET_ONBOARDING_ACCOUNT_STEP = 'SET_ONBOARDING_ACCOUNT_STEP';
// activation
export const CHANGE_CARD_NUMBER = 'CHANGE_CARD_NUMBER';
export const CHANGE_CARD_NUMBER_ERROR_MESSAGE = 'CHANGE_CARD_NUMBER_ERROR_MESSAGE';
export const CHANGE_CARD_EXPIRED_DATE = 'CHANGE_CARD_EXPIRED_DATE';
export const CHANGE_CARD_EXPIRED_DATE_ERROR_MESSAGE = 'CHANGE_CARD_EXPIRED_DATE_ERROR_MESSAGE';
export const CHANGE_CARD_CVC = 'CHANGE_CARD_CVC';
export const CHANGE_CARD_CVC_ERROR_MESSAGE = 'CHANGE_CARD_CVC_ERROR_MESSAGE';
export const SET_ACTIVATION_STEP = 'SET_ACTIVATION_STEP';
export const SET_IS_UPDATE_PASSWORD = 'SET_IS_UPDATE_PASSWORD';

export const START_SUBMIT = 'START_SUBMIT';
export const END_SUBMIT = 'END_SUBMIT';

export const LOGIN_FAILURE = 'LOGIN_FAILURE';

export const RESET = 'RESET';
export const LOCK_TAB = 'LOCK_TAB';
export const UNLOCK_TAB = 'UNLOCK_TAB';

export const EXPAND_SUB_ACCOUNT_INPUT = 'EXPAND_SUB_ACCOUNT_INPUT';

const useLogin = () => {
  const initState = {
    // login,
    username: '',
    usernameErrorMessage: '',
    password: '',
    passwordErrorMessage: '',
    subAccount: '',
    subAccountErrorMessage: '',
    // 2fa
    totp: '',
    totpErrorMessage: '',
    // onboarding
    onboardingStep: 'password',
    newPassword: '',
    newPasswordErrorMessage: '',
    confirmedPassword: '',
    confirmedPasswordErrorMessage: '',
    email: '',
    emailErrorMessage: '',
    companyName: '',
    companyNameErrorMessage: '',
    isUpdatePassword: false,
    // activation
    activationStep: 'stored-value-service',
    cardNumber: '',
    cardNumberErrorMessage: '',
    cardExpiredDate: '',
    cardExpiredDateErrorMessage: '',
    cardCVC: '',
    cardCVCErrorMessage: '',

    loading: false,
    lockTab: false,

    isExpandSubAccountInput: false,
  };

  // login
  const changeUsername = (state, payload) => ({ ...state, username: payload, usernameErrorMessage: '' });
  const changeUsernameErrorMessage = (state, payload) => ({ ...state, usernameErrorMessage: payload });
  const changePassword = (state, payload) => ({ ...state, password: payload, passwordErrorMessage: '' });
  const changePasswordErrorMessage = (state, payload) => ({ ...state, passwordErrorMessage: payload });
  const changeSubAccount = (state, payload) => merge({}, state, { subAccount: payload });
  // 2fa
  const changeTotp = (state, payload) => ({ ...state, totp: payload });
  const changeTotpErrorMessage = (state, payload) => ({ ...state, totpErrorMessage: payload });
  // onboarding
  const changeNewPassword = (state, payload) => merge({}, state, { newPassword: payload });
  const changeConfirmedPassword = (state, payload) => merge({}, state, { confirmedPassword: payload });
  const changeNewPasswordErrorMessage = (state, payload) => ({ ...state, newPasswordErrorMessage: payload });
  const changeConfirmedPasswordErrorMessage = (state, payload) => ({
    ...state,
    confirmedPasswordErrorMessage: payload,
  });
  const changeEmail = (state, payload) => ({ ...state, email: payload });
  const changeEmailErrorMessage = (state, payload) => ({ ...state, emailErrorMessage: payload });
  const changeCompanyName = (state, payload) => ({ ...state, companyName: payload });
  const changeCompanyNameErrorMessage = (state, payload) => ({ ...state, companyNameErrorMessage: payload });
  const setIsUpdatePassword = state => ({ ...state, isUpdatePassword: true });
  const setOnboardingPasswordStep = state => ({ ...state, onboardingStep: 'password' });
  const setOnboardingAccountStep = state => ({ ...state, onboardingStep: 'account' });
  const setActivationStep = (state, payload) => ({ ...state, activationStep: payload });
  const changeCardNumber = (state, payload) => ({ ...state, cardNumber: payload });
  const changeCardNumberErrorMessage = (state, payload) => ({ ...state, cardNumberErrorMessage: payload });
  const changeCardExpiredDate = (state, payload) => ({ ...state, cardExpiredDate: payload });
  const changeCardExpiredDateErrorMessage = (state, payload) => ({ ...state, cardExpiredDateErrorMessage: payload });
  const changeCardCVC = (state, payload) => ({ ...state, cardCVC: payload });
  const changeCardCVCErrorMessage = (state, payload) => ({ ...state, cardCVCErrorMessage: payload });

  const startSubmit = state => merge({}, state, { loading: true });
  const endSubmit = state => merge({}, state, { loading: false });

  const loginFailure = (state, { code, message }) => {
    switch (code) {
      case 'user_not_found':
        return merge({}, state, {
          usernameErrorMessage: message,
          passwordErrorMessage: '',
          subAccountErrorMessage: '',
          loading: false,
        });
      case 'sub_account_not_found':
        return merge({}, state, {
          usernameErrorMessage: '',
          passwordErrorMessage: '',
          subAccountErrorMessage: message,
          loading: false,
        });
      default:
        return merge({}, state, {
          usernameErrorMessage: '',
          passwordErrorMessage: message,
          subAccountErrorMessage: '',
          loading: false,
        });
    }
  };

  const reset = () => initState;

  const lockTab = state => merge({}, state, { lockTab: true });
  const unlockTab = state => merge({}, state, { lockTab: false });

  const expandSubAccountInput = state => ({ ...state, isExpandSubAccountInput: true });

  const reducer = createReducer(
    {
      // login
      [CHANGE_USERNAME]: changeUsername,
      [CHANGE_USERNAME_ERROR_MESSAGE]: changeUsernameErrorMessage,
      [CHANGE_SUB_ACCOUNT]: changeSubAccount,
      [CHANGE_PASSWORD]: changePassword,
      [CHANGE_PASSWORD_ERROR_MESSAGE]: changePasswordErrorMessage,
      [LOGIN_FAILURE]: loginFailure,
      // 2fa
      [CHANGE_TOTP]: changeTotp,
      [CHANGE_TOTP_ERROR_MESSAGE]: changeTotpErrorMessage,
      // onboarding
      [CHANGE_NEW_PASSWORD]: changeNewPassword,
      [CHANGE_NEW_PASSWORD_ERROR_MESSAGE]: changeNewPasswordErrorMessage,
      [CHANGE_CONFIRMED_PASSWORD]: changeConfirmedPassword,
      [CHANGE_CONFIRMED_PASSWORD_ERROR_MESSAGE]: changeConfirmedPasswordErrorMessage,
      [CHANGE_EMAIL]: changeEmail,
      [CHANGE_EMAIL_ERROR_MESSAGE]: changeEmailErrorMessage,
      [CHANGE_COMPANY_NAME]: changeCompanyName,
      [CHANGE_COMPANY_NAME_ERROR_MESSAGE]: changeCompanyNameErrorMessage,
      [SET_ONBOARDING_PASSWORD_STEP]: setOnboardingPasswordStep,
      [SET_ONBOARDING_ACCOUNT_STEP]: setOnboardingAccountStep,
      [SET_IS_UPDATE_PASSWORD]: setIsUpdatePassword,
      // activation
      [SET_ACTIVATION_STEP]: setActivationStep,
      [CHANGE_CARD_NUMBER]: changeCardNumber,
      [CHANGE_CARD_EXPIRED_DATE]: changeCardExpiredDate,
      [CHANGE_CARD_CVC]: changeCardCVC,
      [CHANGE_CARD_NUMBER_ERROR_MESSAGE]: changeCardNumberErrorMessage,
      [CHANGE_CARD_EXPIRED_DATE_ERROR_MESSAGE]: changeCardExpiredDateErrorMessage,
      [CHANGE_CARD_CVC_ERROR_MESSAGE]: changeCardCVCErrorMessage,

      [START_SUBMIT]: startSubmit,
      [END_SUBMIT]: endSubmit,
      [RESET]: reset,
      [LOCK_TAB]: lockTab,
      [UNLOCK_TAB]: unlockTab,
      [EXPAND_SUB_ACCOUNT_INPUT]: expandSubAccountInput,
    },
    initState
  );

  const [state, dispatch] = useReducer(reducer, initState);
  const history = useHistory();

  useEffect(() => {
    const onKeyDown = event => {
      // 當最後一個 input focus 時鎖住 tab 鍵
      if (state.lockTab && event.which === 9) {
        event.preventDefault();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [state.lockTab]);

  const onLogin = () => {
    const loginFormCheck = () => {
      const usernameValidation = fieldCheck(REQUIRE, state.username);
      const passwordValidation = fieldCheck(REQUIRE, state.password);

      dispatch({ type: CHANGE_USERNAME_ERROR_MESSAGE, payload: usernameValidation.error });
      dispatch({ type: CHANGE_PASSWORD_ERROR_MESSAGE, payload: passwordValidation.error });

      return usernameValidation.success && passwordValidation.success;
    };

    if (!loginFormCheck()) return;

    dispatch({ type: START_SUBMIT });

    repo
      .login(state.username, state.password, state.subAccount)
      .then(value => {
        // ---以下流程為暫時解---

        let scopes = value.scopes.split(' ');
        let scopeIsUser = scopes.includes(SCOPES.USER);
        let scopeIsSubAccount = scopes.includes(SCOPES.USER_SUB_ACCOUNT);
        let scopeIsUpdatePassword = scopes.includes(SCOPES.UPDATE_USER_PASSWORD);
        let scopeIs2FA = scopes.includes(SCOPES.USER_2FA_LOGIN);

        if (scopeIsUpdatePassword && !scopeIsSubAccount) {
          if (!value.hasBeenLogin) {
            analytics.track('用戶首次登入');
          }
          history.push({ pathname: PATH.LOGIN, hash: STEPS.ONBOARDING });
        } else if (scopeIsUser || scopeIsSubAccount) {
          dispatch({
            type: RESET,
          });
          history.push(PATH.PROFILE);
        } else if (scopeIs2FA) {
          history.push({ pathname: PATH.LOGIN, hash: STEPS.TWOFA_LOGIN });
        }
      })
      .catch(error => {
        dispatch({
          type: LOGIN_FAILURE,
          payload: errorHandingWithCode(error),
        });
      })
      .finally(() => {
        dispatch({ type: END_SUBMIT });
      });
  };

  const onUpdatePassword = () => {
    const updatePasswordFormCheck = () => {
      const newPasswordValidation = fieldCheck(PASSWORD, state.newPassword);
      const confirmedpasswordValidation = fieldCheck(PASSWORD, state.confirmedPassword);

      dispatch({ type: CHANGE_NEW_PASSWORD_ERROR_MESSAGE, payload: newPasswordValidation.error });
      dispatch({ type: CHANGE_CONFIRMED_PASSWORD_ERROR_MESSAGE, payload: confirmedpasswordValidation.error });

      return newPasswordValidation.success && confirmedpasswordValidation.success;
    };

    if (!updatePasswordFormCheck()) return;

    if (state.newPassword !== state.confirmedPassword) {
      return dispatch({
        type: CHANGE_CONFIRMED_PASSWORD_ERROR_MESSAGE,
        payload: '兩次密碼輸入不相同',
      });
    }

    dispatch({ type: SET_ONBOARDING_ACCOUNT_STEP });
  };

  const onUpdateAccount = () => {
    const updateAccountFormCheck = () => {
      const emailValidation = fieldCheck(EMAIL, state.email);
      const companyNameValidation = fieldCheck(REQUIRE, state.companyName);

      dispatch({ type: CHANGE_EMAIL_ERROR_MESSAGE, payload: emailValidation.error });
      dispatch({ type: CHANGE_COMPANY_NAME_ERROR_MESSAGE, payload: companyNameValidation.error });

      return emailValidation.success && companyNameValidation.success;
    };

    if (!updateAccountFormCheck()) return;

    dispatch({ type: START_SUBMIT });

    // ---以下流程為暫時解---

    if (state.isUpdatePassword) {
      return repo
        .updateUserInfo(state.email, state.companyName)
        .then(() => {
          history.push(PATH.PROFILE);
        })

        .finally(() => [dispatch({ type: END_SUBMIT })]);
    } else {
      repo
        .changePassword(state.password, state.newPassword)
        .then(() => {
          dispatch({ type: SET_IS_UPDATE_PASSWORD });
          return repo.updateUserInfo(state.email, state.companyName).then(() => {
            history.push(PATH.PROFILE);
          });
        })
        .catch(error => {
          dispatch({ type: SET_ONBOARDING_PASSWORD_STEP });
          dispatch({
            type: CHANGE_CONFIRMED_PASSWORD_ERROR_MESSAGE,
            payload: errorHandler(error),
          });
        })
        .finally(() => {
          dispatch({ type: END_SUBMIT });
        });
    }
  };

  const onAuthTOTP = () => {
    const twoFAFormCheck = () => {
      const twoFAValidation = fieldCheck(TWOFA, state.totp);
      dispatch({ type: CHANGE_TOTP_ERROR_MESSAGE, payload: twoFAValidation.error });
      return twoFAValidation.success;
    };

    if (!twoFAFormCheck()) return;

    dispatch({ type: START_SUBMIT });

    repo
      .authTotp(state.totp)
      .then(value => {
        if (value.scopes.includes(SCOPES.UPDATE_USER_PASSWORD)) {
          history.push({ pathname: PATH.LOGIN, hash: STEPS.RESET_PASSWORD });
        } else {
          history.push(PATH.PROFILE);
        }
      })
      .catch(error => {
        dispatch({
          type: CHANGE_TOTP_ERROR_MESSAGE,
          payload: errorHandler(error),
        });
      })
      .finally(() => {
        dispatch({ type: END_SUBMIT });
      });
  };

  const onCompleteBindCard = () => {
    const bindCardFormCheck = () => {
      const cardNumberValidation = fieldCheck(CREDIT_CARD_NUMBER, state.cardNumber);
      const cardExpiredDateValidation = fieldCheck(CREDIT_CARD_EXPIRED_DATE, state.cardExpiredDate);
      const cardCVCValidation = fieldCheck(CREDIT_CARD_CVC, state.cardCVC);

      dispatch({ type: CHANGE_CARD_NUMBER_ERROR_MESSAGE, payload: cardNumberValidation.error });
      dispatch({ type: CHANGE_CARD_EXPIRED_DATE_ERROR_MESSAGE, payload: cardExpiredDateValidation.error });
      dispatch({ type: CHANGE_CARD_CVC_ERROR_MESSAGE, payload: cardCVCValidation.error });

      return cardNumberValidation.success && cardExpiredDateValidation.success && cardCVCValidation.success;
    };

    if (!bindCardFormCheck()) return;

    history.push(PATH.PAYMENT);
  };

  const onLockTab = useCallback(() => {
    dispatch({ type: LOCK_TAB });
  }, [dispatch]);

  const onUnlockTab = useCallback(() => {
    dispatch({ type: UNLOCK_TAB });
  }, [dispatch]);

  return {
    state,
    dispatch,
    onLogin,
    onUpdatePassword,
    onUpdateAccount,
    onAuthTOTP,
    onCompleteBindCard,
    onLockTab,
    onUnlockTab,
  };
};

const [LoginProvider, useLoginContext] = constate(useLogin);

export { LoginProvider, useLoginContext };
